/**
 * Authentication hook
 * Provides convenient access to auth state and actions
 */

import { useEffect } from 'react';
import { useAuthStore } from '../stores/auth.store';

export interface UseAuthReturn {
  // State
  user: ReturnType<typeof useAuthStore.getState>['user'];
  quota: ReturnType<typeof useAuthStore.getState>['quota'];
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string, profession?: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

/**
 * Hook for authentication state and actions
 * Automatically fetches user data on mount if authenticated
 */
export function useAuth(): UseAuthReturn {
  const {
    user,
    quota,
    isLoading,
    isAuthenticated,
    error,
    login,
    register,
    logout,
    fetchMe,
    clearError,
  } = useAuthStore();

  // Fetch user data on mount to restore session
  useEffect(() => {
    // Only fetch if we haven't already loaded user data
    if (!user && !isLoading) {
      fetchMe();
    }
  }, []);

  return {
    user,
    quota,
    isLoading,
    isAuthenticated,
    error,
    login,
    register,
    logout,
    clearError,
  };
}
